import React, { useMemo } from 'react';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import remarkDirective from 'remark-directive';
import remarkRehype from 'remark-rehype';
import rehypeReact from 'rehype-react';
import { visit } from 'unist-util-visit';

// Basic directive components
const ButtonDirective = (props: any) => {
  const { to, href, title, stretch, primary, children, ...rest } = props;
  const route = to || href;
  const isFilled = stretch !== 'false';
  
  // Extract text content from children if it exists
  let buttonText = title;
  if (!buttonText && children) {
    if (Array.isArray(children)) {
      buttonText = children.map(c => typeof c === 'string' ? c : c?.props?.children || '').join('');
    } else if (typeof children === 'string') {
      buttonText = children;
    } else if (children?.props?.children) {
      buttonText = children.props.children;
    }
  }
  if (!buttonText) {
    buttonText = route || 'Button';
  }
  
  return (
    <button
      {...rest}
      className={`button ${primary !== undefined ? 'cta' : ''}`}
      style={{
        width: isFilled ? '100%' : 'auto',
      }}
      onClick={() => {
        if (route) {
          window.location.href = route;
        }
      }}
    >
      {buttonText}
    </button>
  );
};

const CenterDirective = (props: any) => (
  <div {...props} style={{ textAlign: 'center', ...props.style }} />
);

const RightDirective = (props: any) => (
  <div {...props} style={{ textAlign: 'right', ...props.style }} />
);

const LeftDirective = (props: any) => (
  <div {...props} style={{ textAlign: 'left', ...props.style }} />
);

const RowDirective = (props: any) => (
  <div {...props} style={{ display: 'flex', flexDirection: 'row', ...props.style }} />
);

const IframeDirective = (props: any) => (
  <iframe {...props} style={{ width: '100%', border: 'none', ...props.style }} />
);

const HrDirective = () => <hr style={{ margin: '20px 0' }} />;

const directiveMap: Record<string, string | React.ComponentType<any>> = {
  // HTML elements
  br: 'br',
  hr: HrDirective,
  
  // Headings (mapped to native HTML)
  h: 'h1',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  
  // Text formatting
  b: 'strong',
  u: 'u',
  strong: 'strong',
  
  // Layout
  center: CenterDirective,
  right: RightDirective,
  left: LeftDirective,
  row: RowDirective,
  
  // Interactive
  button: ButtonDirective,
  iframe: IframeDirective,
  
  // Table elements
  table: 'table',
  thead: 'thead',
  tbody: 'tbody',
  tr: 'tr',
  th: 'th',
  td: 'td',
  
  // Add more custom components here as needed
};

function remarkCustomDirectives() {
  return (tree: any) => {
    visit(tree, (node: any, _index: number, _parent: any) => {
      if (!node || !node.type) return;
      
      if (
        node.type === 'textDirective' ||
        node.type === 'leafDirective' ||
        node.type === 'containerDirective'
      ) {
        
        // Parse attributes from label if present (format: [XATTRSXkey="value" key2="value2"])
        const parsedAttributes = node.attributes || {};
        
        // Helper function to extract all text content from a node tree
        const extractText = (n: any): string => {
          if (!n) return '';
          if (n.type === 'text') return n.value || '';
          if (n.children && Array.isArray(n.children)) {
            return n.children.map(extractText).join('');
          }
          return '';
        };
        
        // Extract all text from children
        const labelText = node.children ? node.children.map(extractText).join('') : '';
        
        if (labelText && labelText.startsWith('XATTRSX')) {
          const attrsString = labelText.substring(7); // Remove "XATTRSX"
          
          // Parse key="value" pairs (with quotes) - handle all matches
          const attrPairs = attrsString.split(/\s+(?=\w+=)/); // Split on space before key=
          for (const pair of attrPairs) {
            const quotedMatch = pair.match(/^(\w+)="([^"]*)"$/);
            if (quotedMatch) {
              parsedAttributes[quotedMatch[1]] = quotedMatch[2];
            } else {
              const unquotedMatch = pair.match(/^(\w+)=(.+)$/);
              if (unquotedMatch) {
                parsedAttributes[unquotedMatch[1]] = unquotedMatch[2];
              }
            }
          }
        }
        
        // Always clear children for directives with attributes to prevent label rendering
        if (labelText && labelText.startsWith('XATTRSX')) {
          node.children = [];
        }
        
        const data = node.data || (node.data = {});
        const mappedElement = directiveMap[node.name];
        
        // Clear any children for self-closing elements like br
        if (mappedElement === 'br' || mappedElement === 'hr') {
          node.children = [];
        }
        
        if (mappedElement) {
          if (typeof mappedElement === 'string') {
            // HTML element
            data.hName = mappedElement;
            data.hProperties = {
              ...parsedAttributes,
            };
          } else {
            // React component - use a special marker element
            data.hName = 'directive-component';
            data.hProperties = {
              'data-directive-name': node.name,
              ...parsedAttributes,
            };
          }
        } else {
          // Fallback to a div with directive class for undefined directives
          data.hName = 'div';
          data.hProperties = {
            className: `directive directive-${node.name}`,
            'data-directive': node.name,
            ...parsedAttributes,
          };
        }
      }
    });
  };
}

export default function MarkdownViewer({ markdown, testId }: { markdown: string | null; testId?: string }) {
  const md = markdown || '';

  const processedMarkdown = useMemo(() => {
    let result = md;
    // Convert directives with attributes to label format for manual parsing
    // ::button{title="login" to="/login"} -> ::button[XATTRSXtitle="login" to="/login"]
    result = result.replace(/::(\w+)\{([^}]+)\}/g, (match, directiveName, attrs) => {
      // Remove escaped backslashes from quotes
      const cleanedAttrs = attrs.replace(/\\"/g, '"').trim();
      // Store attributes in label syntax with XATTRSX marker for our plugin to parse
      return `\n\n::${directiveName}[XATTRSX${cleanedAttrs}]\n\n`;
    });
    return result;
  }, [md]);
  
  const remarkPlugins: any = useMemo(() => [
    remarkGfm,
    remarkDirective,
    remarkCustomDirectives,
  ], []);
  
  const rehypeComponents: any = useMemo(() => ({
    'directive-component': (props: any) => {
      const directiveName = props['data-directive-name'];
      const Component = directiveMap[directiveName];
      if (Component && typeof Component !== 'string') {
        // Extract only the custom attributes (not data-directive-name or children)
        const { 'data-directive-name': _, children: _children, ...componentProps } = props;
        return (
          <span style={{ all: 'revert' }}>
            <Component {...componentProps} />
          </span>
        );
      }
      return <div {...props} />;
    },
  }), []);
  
  const content = useMemo(() => {
    try {
      const processor = unified()
        .use(remarkParse)
        .use(remarkPlugins)
        .use(remarkRehype)
        .use(rehypeReact as any, {
          createElement: React.createElement,
          Fragment: React.Fragment,
          components: rehypeComponents,
        });
      return (processor.processSync(processedMarkdown) as any).result as React.ReactElement;
    } catch (e) {
      console.error('MarkdownViewer processing error:', e);
      return <div style={{ whiteSpace: 'pre-wrap' }}>{processedMarkdown}</div>;
    }
  }, [processedMarkdown, remarkPlugins, rehypeComponents]);
  
  return (
    <div className="markdown-viewer" data-testid={testId}>
      {content}
    </div>
  );
}
